import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  useTheme,
  InputAdornment,
} from '@mui/material';
import {
  Email as EmailIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import supabase from '../../supabaseClient';
import madrasatiLightLogo from '../../assets/madrasati-light.svg';
import madrasatiDarkLogo from '../../assets/madrasati-dark.svg';

function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Normalize the email
    const normalizedEmail = email.toLowerCase().trim();

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail);

      if (error) throw error;

      setSuccess('If an account exists with this email, you will receive password reset instructions.');
    } catch (error) {
      setError('Failed to process request. Please try again.');
      console.error('Reset password error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        overflow: 'hidden',
        padding: { xs: 2, md: 4 },
      }}
    >
      {/* Decorative circles */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(52, 152, 219, 0.09) 0%, rgba(41, 128, 185, 0.15) 100%)`,
          top: '-100px',
          right: '-100px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, rgba(41, 128, 185, 0.12) 0%, rgba(52, 152, 219, 0.18) 100%)`,
          bottom: '50px',
          left: '50px',
        }}
      />

      {/* Centered Forgot Password Form */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '500px',
          borderRadius: '24px',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          padding: { xs: 3, md: 5 },
          zIndex: 1,
        }}
      >
        {/* Logo at top */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 4,
          }}
        >
          <Box
            component="img"
            src={theme.palette.mode === 'dark' ? madrasatiDarkLogo : madrasatiLightLogo}
            alt="Madrasati Logo"
            sx={{
              width: '100%',
              maxWidth: '200px',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.secondary.contrastText,
            mb: 3,
            textAlign: 'center',
          }}
        >
          Reset Password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            placeholder="Email *"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.9)',
                '& fieldset': {
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(255, 255, 255, 0.5)',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px',
                },
              },
              '& .MuiInputBase-input': {
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'rgba(0, 0, 0, 0.87)',
                '&::placeholder': {
                  color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : 'rgba(0, 0, 0, 0.6)',
                  opacity: 1,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: theme.palette.mode === 'dark' ? theme.palette.text.secondary : 'rgba(0, 0, 0, 0.6)' }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: '12px',
              textTransform: 'none',
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Instructions'}
          </Button>
        </form>

        <Typography variant="body2" align="center" sx={{ mt: 2, color: theme.palette.secondary.contrastText }}>
          Remember your password?{' '}
          <Button
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ textTransform: 'none' }}
          >
            Login here
          </Button>
        </Typography>
      </Box>
    </Box>
  );
}

export default ForgotPasswordForm; 